import {
  AlertTriangle, ArrowRight, Bell, Bot, Calculator, CalendarClock, Camera, CheckCheck,
  CheckCircle2, ChevronDown, ClipboardCheck, FileText, FolderOpen,
  LayoutDashboard, ListTodo, Menu, MessageCircle, Mic, MoreVertical, Paperclip, Phone,
  Receipt, Search, Settings, Smile, Users, Video,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import ManagementDashboardMockup from "@/components/ManagementDashboardMockup";
import siteProgressOne from "@/assets/tiling-zone-b-1.png";
import siteProgressTwo from "@/assets/tiling-zone-b-2.png";
import siteProgressWide from "@/assets/gulf-villa-construction.jpg";

const HeroWorkflowMockup = () => {
  const { t } = useTranslation();
  const navigation = [
    [LayoutDashboard, "overview"], [ListTodo, "tasks"], [CalendarClock, "timeline"],
    [AlertTriangle, "riskNav"], [FolderOpen, "documents"], [FileText, "reports"],
    [ClipboardCheck, "inspect"], [Receipt, "reports"], [Calculator, "create"], [Bot, "search"],
  ] as const;

  return (
    <div className="relative overflow-hidden rounded-[28px] border border-white/15 bg-[#101526] p-3 shadow-[0_34px_90px_rgba(0,0,0,0.42)] sm:p-4">
      <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-blue-500/20 blur-[80px]" />
      <div className="relative mb-3 flex items-center justify-between px-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/50">{t("heroMockup.flowLabel")}</p>
        <div className="flex items-center gap-1.5 text-[10px] text-emerald-300">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.85)]" />
          {t("heroMockup.live")}
        </div>
      </div>

      <div className="relative grid gap-3 lg:grid-cols-[minmax(320px,0.82fr)_82px_minmax(0,1.5fr)] lg:items-start">
        {/* WhatsApp group */}
        <div className="flex min-h-[650px] w-full max-w-[430px] justify-self-center flex-col overflow-hidden rounded-2xl bg-[#efeae2] text-left text-slate-800 shadow-[0_20px_50px_rgba(0,0,0,0.28)] lg:h-[650px] lg:min-h-0 lg:max-w-none lg:self-start">
          <div className="flex items-center gap-2 bg-[#075e54] px-3 py-2.5 text-white">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/20">
              <Users className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-semibold">{t("heroMockup.groupTitle")}</p>
              <p className="truncate text-[8px] text-white/70">{t("heroMockup.groupMeta")}</p>
            </div>
            <Video className="h-3.5 w-3.5 text-white/85" />
            <Phone className="h-3.5 w-3.5 text-white/85" />
            <MoreVertical className="h-4 w-4 text-white/85" />
          </div>

          <div
            className="relative flex-1 space-y-2 overflow-hidden p-3"
            style={{
              backgroundImage: "radial-gradient(circle at 20% 18%, rgba(111,128,105,0.08) 0 1px, transparent 1.5px), radial-gradient(circle at 74% 68%, rgba(111,128,105,0.08) 0 1px, transparent 1.5px)",
              backgroundSize: "26px 26px, 31px 31px",
            }}
          >
            <div className="mx-auto w-fit rounded-md bg-[#fff5c4] px-2 py-1 text-[7px] font-medium text-slate-500 shadow-sm">{t("heroMockup.today")}</div>

            <div className="max-w-[94%] rounded-lg rounded-tl-sm bg-white px-2.5 py-2 shadow-sm">
              <p className="text-[8px] font-bold text-[#2563eb]">BANAMIND AI</p>
              <div className="mt-0.5 flex items-start gap-1.5">
                <Bot className="mt-0.5 h-3 w-3 shrink-0 text-blue-600" />
                <p className="text-[9px] leading-snug">{t("heroMockup.overdueEvidenceRequest")}</p>
              </div>
              <p className="mt-1 text-right text-[7px] text-slate-400">09:38</p>
            </div>

            <div className="max-w-[92%] rounded-lg rounded-tl-sm bg-white px-2.5 py-2 shadow-sm">
              <p className="text-[8px] font-bold text-[#d05e2d]">{t("heroMockup.foreman")}</p>
              <p className="mt-0.5 text-[10px] leading-snug">{t("heroMockup.update")}</p>
              <div className="mt-1.5 grid grid-cols-3 gap-1">
                {[
                  [siteProgressOne, "Level 2 work area"],
                  [siteProgressTwo, "Interior finishing progress"],
                  [siteProgressWide, "Villa construction site"],
                ].map(([src, alt]) => (
                  <div key={src} className="relative h-12 overflow-hidden rounded-sm bg-slate-200">
                    <img src={src} alt={alt} className="h-full w-full object-cover" />
                    <div className="absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-black/35 to-transparent" />
                  </div>
                ))}
              </div>
              <p className="mt-1 text-right text-[7px] text-slate-400">09:42</p>
            </div>

            <div className="max-w-[94%] rounded-lg rounded-tl-sm bg-white px-2.5 py-2 shadow-sm">
              <p className="text-[8px] font-bold text-[#2563eb]">BANAMIND AI</p>
              <p className="mt-0.5 text-[9px] leading-snug">{t("heroMockup.reportReady")}</p>
              <div className="mt-1.5 flex items-center gap-2 rounded-md bg-slate-50 p-1.5">
                <div className="flex h-7 w-7 items-center justify-center rounded bg-blue-50"><FileText className="h-3.5 w-3.5 text-blue-500" /></div>
                <div className="min-w-0">
                  <p className="truncate text-[8px] font-semibold">Daily-progress-report.pdf</p>
                  <p className="text-[7px] text-slate-400">PDF · 3.2 MB</p>
                </div>
              </div>
              <p className="mt-1 text-right text-[7px] text-slate-400">09:45</p>
            </div>

            <div className="ml-auto max-w-[88%] rounded-lg rounded-tr-sm bg-[#d9fdd3] px-2.5 py-2 shadow-sm">
              <p className="text-[9px] leading-snug">{t("heroMockup.contractRequest")}</p>
              <div className="mt-1 flex items-center justify-end gap-0.5 text-[7px] text-slate-400">09:48 <CheckCheck className="h-2.5 w-2.5 text-sky-500" /></div>
            </div>

            <div className="max-w-[94%] rounded-lg rounded-tl-sm bg-white px-2.5 py-2 shadow-sm">
              <p className="text-[8px] font-bold text-[#2563eb]">BANAMIND AI</p>
              <p className="mt-0.5 text-[9px] leading-snug">{t("heroMockup.contractFound")}</p>
              <div className="mt-1.5 flex items-center gap-2 rounded-md bg-slate-50 p-1.5">
                <div className="flex h-7 w-7 items-center justify-center rounded bg-red-50"><FileText className="h-3.5 w-3.5 text-red-500" /></div>
                <div className="min-w-0">
                  <p className="truncate text-[8px] font-semibold">Waterproofing-contract_signed.pdf</p>
                  <p className="text-[7px] text-slate-400">Signed contract · PDF · 2.1 MB</p>
                </div>
              </div>
              <p className="mt-1 text-right text-[7px] text-slate-400">09:48</p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 bg-[#f0f2f5] p-2">
            <Smile className="h-4 w-4 shrink-0 text-slate-500" />
            <Paperclip className="h-4 w-4 shrink-0 text-slate-500" />
            <div className="flex h-8 flex-1 items-center rounded-full bg-white px-3 text-[8px] text-slate-400">{t("heroMockup.messagePlaceholder")}</div>
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#00a884] text-white"><Mic className="h-3.5 w-3.5" /></div>
          </div>
        </div>

        {/* AI agent bridge */}
        <div className="flex flex-col items-center justify-center rounded-2xl border border-blue-400/20 bg-blue-500/10 px-2 py-3 lg:self-stretch">
          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-500 text-white shadow-[0_8px_24px_rgba(59,130,246,0.35)]"><Bot className="h-5 w-5" /></div>
          <p className="mt-2 text-center text-[9px] font-bold uppercase tracking-wider text-blue-200">{t("heroMockup.agent")}</p>
          <div className="mt-3 flex flex-wrap justify-center gap-1 lg:flex-col lg:items-stretch">
            {[[Camera, "capture"], [ClipboardCheck, "create"], [Search, "search"], [AlertTriangle, "inspect"]].map(([Icon, key]) => {
              const ActionIcon = Icon as typeof Camera;
              return <div key={String(key)} className="flex items-center gap-1 rounded-md bg-white/10 px-1.5 py-1 text-[8px] font-semibold text-white/70"><ActionIcon className="h-2.5 w-2.5 text-blue-300" />{t(`heroMockup.${String(key)}`)}</div>;
            })}
          </div>
          <ArrowRight className="mt-3 hidden h-4 w-4 text-blue-300 lg:block" />
        </div>

        {/* Management dashboard */}
        <ManagementDashboardMockup />
        <div className="hidden min-h-[548px] overflow-hidden rounded-2xl bg-[#f5f7fb] text-left text-slate-900 shadow-[0_20px_50px_rgba(0,0,0,0.28)]">
          <aside className="hidden w-12 shrink-0 flex-col items-center bg-[#111a35] py-3 sm:flex">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-500 text-[11px] font-black text-white">B</div>
            <div className="mt-5 flex flex-1 flex-col items-center gap-2">
              {navigation.map(([Icon], index) => <div key={index} className={`flex h-7 w-7 items-center justify-center rounded-md ${index === 0 ? "bg-blue-500 text-white" : "text-white/45"}`}><Icon className="h-3.5 w-3.5" /></div>)}
            </div>
            <Settings className="h-3.5 w-3.5 text-white/40" />
          </aside>

          <div className="min-w-0 flex-1">
            <div className="flex h-[52px] items-center justify-between border-b border-slate-200 bg-white px-3">
              <div className="flex min-w-0 items-center gap-2">
                <Menu className="h-4 w-4 text-slate-400 sm:hidden" />
                <div className="min-w-0">
                  <div className="flex items-center gap-1"><p className="truncate text-[11px] font-bold">{t("heroMockup.dashboard")}</p><ChevronDown className="h-3 w-3 text-slate-400" /></div>
                  <p className="truncate text-[8px] text-slate-400">{t("heroMockup.project")}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="hidden h-7 items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2 text-[8px] text-slate-400 md:flex"><Search className="h-3 w-3" />{t("heroMockup.searchProject")}</div>
                <Bell className="h-3.5 w-3.5 text-slate-400" />
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-blue-400 to-indigo-600 text-[8px] font-bold text-white">OM</div>
              </div>
            </div>

            <div className="border-b border-slate-200 bg-white px-3 py-2 sm:hidden">
              <div className="flex gap-1 overflow-hidden">{navigation.slice(0, 4).map(([Icon, key], index) => <div key={key} className={`flex items-center gap-1 rounded-md px-2 py-1 text-[7px] font-semibold ${index === 0 ? "bg-blue-50 text-blue-700" : "text-slate-400"}`}><Icon className="h-2.5 w-2.5" />{t(`heroMockup.${key}`)}</div>)}</div>
            </div>

            <div className="p-3">
              <div className="mb-2 flex items-center justify-between">
                <div><p className="text-[12px] font-bold">Marina Villa</p><p className="text-[8px] text-slate-400">{t("heroMockup.projectOverview")}</p></div>
                <span className="rounded-full bg-emerald-100 px-2 py-1 text-[8px] font-bold text-emerald-700">{t("heroMockup.onTrack")}</span>
              </div>

              <div className="grid grid-cols-2 gap-2 xl:grid-cols-4">
                {[["68%", t("heroMockup.progress"), "text-blue-600"], ["24", t("heroMockup.verified"), "text-emerald-600"], ["14", t("heroMockup.openTasks"), "text-slate-900"], ["2", t("heroMockup.risks"), "text-amber-600"]].map(([value, label, color]) => (
                  <div key={String(label)} className="rounded-lg border border-slate-200 bg-white p-2.5 shadow-[0_2px_8px_rgba(15,23,42,0.03)]"><p className={`text-[16px] font-bold tracking-tight ${color}`}>{value}</p><p className="mt-0.5 truncate text-[7px] text-slate-400">{label}</p></div>
                ))}
              </div>

              <div className="mt-2 grid grid-cols-2 gap-2 xl:grid-cols-4">
                {[
                  [ClipboardCheck, "3", t("features.aiInspection.label"), "bg-emerald-50 text-emerald-600"],
                  [FolderOpen, "128", t("features.documentIntel.label"), "bg-violet-50 text-violet-600"],
                  [Receipt, "2", t("features.trackPerformance.label"), "bg-sky-50 text-sky-600"],
                  [Calculator, "1", t("features.aiEstimating.label"), "bg-indigo-50 text-indigo-600"],
                ].map(([Icon, value, label, color]) => {
                  const ModuleIcon = Icon as typeof ClipboardCheck;
                  return (
                    <div key={String(label)} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white p-2">
                      <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${color}`}><ModuleIcon className="h-3.5 w-3.5" /></div>
                      <div className="min-w-0"><p className="text-[11px] font-bold">{String(value)}</p><p className="truncate text-[6px] text-slate-400">{String(label)}</p></div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-2 grid gap-2 xl:grid-cols-[1.48fr_0.92fr]">
                <div className="rounded-xl border border-slate-200 bg-white p-3">
                  <div className="flex items-center justify-between"><div><p className="text-[10px] font-bold">{t("heroMockup.timelineTitle")}</p><p className="text-[7px] text-slate-400">{t("heroMockup.planActual")}</p></div><span className="text-[7px] text-slate-400">{t("heroMockup.week18")}</span></div>
                  <div className="mt-3 space-y-3">
                    {[["structure", "82%", "w-[82%]", "bg-emerald-500", "w-[88%]"], ["mep", "46%", "w-[46%]", "bg-blue-500", "w-[55%]"], ["finishes", "18%", "w-[18%]", "bg-violet-500", "w-[25%]"]].map(([key, value, actual, color, plan]) => (
                      <div key={key}><div className="mb-1 flex justify-between text-[7px] text-slate-500"><span>{t(`heroMockup.${key}`)}</span><span className="font-semibold text-slate-700">{value}</span></div><div className="relative h-2 overflow-hidden rounded-full bg-slate-100"><div className={`absolute inset-y-0 left-0 rounded-full bg-slate-200 ${plan}`} /><div className={`absolute inset-y-0 left-0 rounded-full ${actual} ${color}`} /></div></div>
                    ))}
                  </div>
                  <div className="mt-3 grid grid-cols-7 items-end gap-1 border-t border-slate-100 pt-3">{[34, 48, 42, 61, 56, 78, 68].map((height, index) => <div key={index} className="flex h-10 items-end rounded-sm bg-blue-50"><div className="w-full rounded-sm bg-blue-500/75" style={{ height: `${height}%` }} /></div>)}</div>
                </div>

                <div className="space-y-2">
                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-2.5">
                    <div className="flex items-center justify-between"><div className="flex items-center gap-1.5"><AlertTriangle className="h-3 w-3 text-amber-600" /><p className="text-[9px] font-bold text-amber-900">{t("heroMockup.attention")}</p></div><span className="rounded bg-amber-100 px-1 py-0.5 text-[6px] font-bold text-amber-700">AI</span></div>
                    <p className="mt-2 text-[8px] font-semibold leading-snug text-slate-700">{t("heroMockup.riskItem")}</p><p className="mt-1 text-[7px] leading-snug text-slate-500">{t("heroMockup.riskMeta")}</p>
                  </div>
                  <div className="rounded-xl border border-blue-200 bg-blue-50 p-2.5">
                    <div className="flex items-center gap-1.5"><FileText className="h-3 w-3 text-blue-600" /><p className="text-[9px] font-bold text-blue-900">{t("heroMockup.report")}</p></div>
                    <div className="mt-2 flex items-center justify-between"><p className="text-[8px] text-slate-600">{t("heroMockup.ready")}</p><span className="rounded bg-blue-600 px-2 py-1 text-[7px] font-semibold text-white">{t("heroMockup.view")}</span></div>
                  </div>
                </div>
              </div>

              <div className="mt-2 grid gap-2 xl:grid-cols-2">
                <div className="rounded-xl border border-slate-200 bg-white p-2.5"><div className="flex items-center justify-between"><p className="text-[9px] font-bold">{t("heroMockup.nextTask")}</p><span className="text-[7px] font-bold text-blue-600">72%</span></div><p className="mt-1 text-[7px] text-slate-400">{t("heroMockup.nextTaskMeta")}</p><div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full w-[72%] rounded-full bg-blue-500" /></div></div>
                <div className="rounded-xl border border-slate-200 bg-white p-2.5"><div className="flex items-center justify-between"><p className="text-[9px] font-bold">{t("heroMockup.recentActivity")}</p><span className="text-[7px] text-slate-400">{t("heroMockup.live")}</span></div><div className="mt-2 flex items-center gap-2"><div className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-50"><CheckCheck className="h-3 w-3 text-emerald-600" /></div><p className="min-w-0 flex-1 truncate text-[7px] text-slate-500">{t("heroMockup.inspectionPassed")}</p><span className="text-[6px] text-slate-300">{t("heroMockup.now")}</span></div></div>
              </div>

              <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <div className="flex h-5 w-5 items-center justify-center rounded-md bg-blue-50">
                      <MessageCircle className="h-3 w-3 text-blue-600" />
                    </div>
                    <p className="text-[9px] font-bold">{t("features.projectFeed.label")}</p>
                  </div>
                  <div className="flex items-center gap-1 text-[7px] text-emerald-600">
                    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                    {t("heroMockup.live")}
                  </div>
                </div>
                <div className="grid divide-y divide-slate-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
                  <div className="p-2.5">
                    <div className="flex items-center gap-1.5">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-orange-100 text-[7px] font-bold text-orange-700">AF</div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[8px] font-semibold">{t("heroMockup.foreman")}</p>
                        <p className="text-[6px] text-slate-400">09:42 · {t("heroMockup.now")}</p>
                      </div>
                    </div>
                    <p className="mt-1.5 line-clamp-2 text-[7px] leading-snug text-slate-500">{t("heroMockup.update")}</p>
                    <div className="mt-2 flex gap-1">
                      {["from-amber-100 to-stone-400", "from-sky-100 to-slate-400", "from-orange-100 to-zinc-400"].map((color) => (
                        <div key={color} className={`h-7 flex-1 rounded bg-gradient-to-br ${color}`} />
                      ))}
                    </div>
                  </div>

                  <div className="p-2.5">
                    <div className="flex items-center gap-1.5">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-50"><Bot className="h-3 w-3 text-blue-600" /></div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[8px] font-semibold">BANAMIND AI</p>
                        <p className="text-[6px] text-slate-400">09:43 · {t("heroMockup.now")}</p>
                      </div>
                    </div>
                    <div className="mt-2 flex items-start gap-1.5 rounded-md bg-emerald-50 p-2">
                      <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" />
                      <p className="line-clamp-2 text-[7px] leading-snug text-emerald-900">{t("heroMockup.evidenceSaved")}</p>
                    </div>
                  </div>

                  <div className="p-2.5">
                    <div className="flex items-center gap-1.5">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-50"><AlertTriangle className="h-3 w-3 text-amber-600" /></div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[8px] font-semibold">{t("heroMockup.attention")}</p>
                        <p className="text-[6px] text-slate-400">09:44 · AI</p>
                      </div>
                    </div>
                    <p className="mt-2 line-clamp-2 text-[7px] font-semibold leading-snug text-slate-600">{t("heroMockup.riskItem")}</p>
                    <div className="mt-2 flex items-center justify-between rounded-md bg-amber-50 px-2 py-1.5">
                      <span className="text-[6px] text-amber-800">{t("heroMockup.riskMeta")}</span>
                      <ArrowRight className="h-2.5 w-2.5 shrink-0 text-amber-600" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HeroWorkflowMockup;
